import React, { useMemo, FocusEvent } from 'react'
import { Autocomplete as MuiAutocomplete, AutocompleteRenderGroupParams, CircularProgress, TextField } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { makeStyles } from 'tss-react/mui'
import { CompanyOption } from 'src/Libs/service-dashboard'
import { J1DownarrowD } from '@e3dc-react/icons'

const useStyles = makeStyles()(theme => ({
  searchField: {
    padding: 0,
  },
  input: {
    '.MuiInputBase-root': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',

      '& .Mui-disabled': {
        outlineColor: theme.palette.grey.inputBorderDisabled,
      },
    },
  },
}))

interface AutocompleteProps<T extends CompanyOption | string> {
  name: string
  initialSearchValue?: string
  maxDisplayedResultCount?: number
  showSuggestionHeader?: boolean
  allowValueCreation?: boolean /* determines if a new value can be chosen/added that is not part of the options */
  onClose?: () => void
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: CompanyOption) => React.ReactNode
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode
  isOptionEqualToValue: (option: CompanyOption, value: CompanyOption) => boolean
  groupBy?: (option: CompanyOption) => string
  value?: T
  options: CompanyOption[]
  handleInputChange: (event: React.SyntheticEvent | null, value: string) => void
  handleChange: (value: T | null) => void
  loading?: boolean
  isMenuOpen?: boolean
  getOptionLabel?: (option: string | CompanyOption) => string
  withTrim?: boolean
  label: string
  disabled?: boolean
}

/**
 * Wrapper component to make Autocomplete work with formik
 */
const Autocomplete = <T extends CompanyOption | string>({
  name,
  value,
  allowValueCreation,
  renderGroup,
  renderOption,
  isOptionEqualToValue,
  groupBy,
  handleChange,
  handleInputChange,
  options,
  loading,
  isMenuOpen,
  onClose,
  getOptionLabel,
  withTrim,
  label,
  disabled,
}: AutocompleteProps<T>): React.ReactElement => {
  const { classes } = useStyles()
  const { handleBlur } = useFormikContext()
  const [, meta] = useField(name)

  const hasError = useMemo<boolean>(() => {
    return Boolean(meta.error && meta.touched)
  }, [meta])

  const helperText = useMemo<string | undefined>(() => {
    return meta.error
  }, [meta.error])

  const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
    handleBlur(e)

    if (withTrim) {
      handleChange(e.target.value?.trim() as T)
    }
  }

  return (
    <MuiAutocomplete
      className={classes.searchField}
      classes={{ paper: classes.searchField }}
      onInputChange={(event, value) => handleInputChange(event, value)}
      onChange={(event, value) => handleChange(value as T)}
      value={value}
      open={isMenuOpen}
      openOnFocus={false}
      onClose={onClose}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      options={options}
      loading={loading}
      filterOptions={x => x}
      freeSolo={allowValueCreation}
      groupBy={groupBy}
      renderGroup={renderGroup}
      renderOption={renderOption}
      popupIcon={<J1DownarrowD />}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          name={name}
          helperText={hasError ? helperText : undefined}
          error={hasError}
          required
          label={label}
          className={classes.input}
          onBlur={onBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default Autocomplete
